import React, { createElement as _createElement } from 'react';
import classnames from 'classnames';
import { AutoSizer, CellMeasurer, CellMeasurerCache, Grid, ScrollSync } from 'react-virtualized';
import { ValuesCache } from '@leverege/value-cache';
import { cellRangeRenderer, Loading, NoData } from "./util/index.js";
import Content from "../../Content.js";
import Flex from "../../Flex.js";
import S from '../../VirtualizedTableRenderer.css';
import TableV2Footer from "../TableV2Footer.js";
import { renderDynamicComponent } from "../util/index.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export default class VirtualizedTableRenderer extends React.Component {
  constructor(props) {
    super(props);
    const {
      bodyRef
    } = props;
    this.cache = new ValuesCache();
    this.headerGridRef = /*#__PURE__*/React.createRef();
    this.bodyGridRef = bodyRef || /*#__PURE__*/React.createRef();
    this.headerCellMeasurerCache = new CellMeasurerCache({
      defaultWidth: 150,
      fixedWidth: true,
      fixedHeight: false,
      keyMapper: (rowIndex, columnIndex) => {
        const row = props.rows[rowIndex];
        if (row) {
          const columnId = row.cells?.[columnIndex]?.column?.id;
          if (columnId) {
            return columnId;
          }
        }
        return `${rowIndex}-${columnIndex}`;
      }
    });
    this.bodyCellMeasurerCache = new CellMeasurerCache({
      defaultWidth: 150,
      fixedWidth: true,
      fixedHeight: false,
      keyMapper: (rowIndex, columnIndex) => {
        const row = props.rows[rowIndex];
        if (row) {
          const columnId = row.cells?.[columnIndex]?.column?.id;
          if (columnId) {
            return `${row.id}-${columnId}`;
          }
        }
        return `${rowIndex}-${columnIndex}`;
      }
    });
  }
  get headerScrollbarOffset() {
    const bodyGrid = this.bodyGridRef.current?._scrollingContainer;
    if (bodyGrid) {
      return bodyGrid.offsetWidth - bodyGrid.clientWidth;
    }
    return 0;
  }
  componentDidMount() {
    // Can't find another solution to the issue of header grid not resizing before first render
    setTimeout(() => {
      this.headerGridRef?.current?.recomputeGridSize();
      this.bodyGridRef?.current?.recomputeGridSize();
      this.forceUpdate();
    }, 1);
  }
  cellRangeRenderer = cellRangeRenderer.bind(this);
  columnWidthGetter = (columns, tableWidth) => {
    const getWidth = column => Math.min(Math.max(column.width, column.minWidth), column.maxWidth);
    return ({
      index
    }) => {
      const width = getWidth(columns[index]);
      const isLastColumn = index === columns.length - 1;
      if (isLastColumn) {
        const accumWidth = columns.reduce((prev, column, i) => {
          if (i === index) {
            return prev;
          }
          return prev + getWidth(column);
        }, 0);
        const lastWidth = Math.max(tableWidth - accumWidth, width);
        return lastWidth;
      }
      return width;
    };
  };
  onResizeStart = () => {
    window.addEventListener('mousemove', this.onResize);
    window.addEventListener('touchmove', this.onResize);
    window.addEventListener('mouseup', this.onResizeEnd, {
      once: true
    });
    window.addEventListener('touchend', this.onResizeEnd, {
      once: true
    });
  };
  onResize = () => {
    window.requestAnimationFrame(() => {
      if (this.bodyGridRef && this.bodyGridRef.current) {
        this.bodyGridRef.current.recomputeGridSize();
      }
      if (this.headerGridRef && this.headerGridRef.current) {
        this.headerGridRef.current.recomputeGridSize();
      }
    });
  };
  onResizeEnd = event => {
    window.removeEventListener('mousemove', this.onResize);
    window.removeEventListener('touchmove', this.onResize);
  };
  headerCellRendererGetter = columns => {
    const {
      headerClassName,
      headerFlexVariant,
      resizerClassName,
      sortAscClassName,
      sortDescClassName
    } = this.props;

    // eslint-disable-next-line react/no-unstable-nested-components, react/function-component-definition
    return props => {
      const {
        key,
        ...rest
      } = props;
      return /*#__PURE__*/_createElement(HeaderCellRenderer, {
        columns: columns
        /* eslint-disable react/no-this-in-sfc */,
        cellMeasurerCache: this.headerCellMeasurerCache,
        headerClassName: headerClassName,
        sortAscClassName: sortAscClassName,
        sortDescClassName: sortDescClassName,
        headerFlexVariant: headerFlexVariant,
        resizerClassName: resizerClassName,
        onResizeStart: this.onResizeStart,
        onResizeEnd: this.onResizeEnd,
        onResize: this.onResize
        /* eslint-enable react/no-this-in-sfc */,
        ...rest,
        key: key
      });
    };
  };
  bodyCellRenderer = props => {
    const {
      rows,
      cellClassName
    } = this.props;
    const {
      columnIndex,
      parent,
      rowIndex,
      key,
      style
    } = props;
    const row = rows[rowIndex];
    const cell = row.cells[columnIndex];
    const {
      key: cellKey,
      ...cellProps
    } = cell.getCellProps({
      className: cellClassName,
      style
    });
    return /*#__PURE__*/_jsx(CellMeasurer, {
      cache: this.bodyCellMeasurerCache,
      columnIndex: columnIndex,
      parent: parent,
      rowIndex: rowIndex,
      children: /*#__PURE__*/_createElement("div", {
        ...cellProps,
        key: cellKey
      }, cell.render('Cell'))
    }, key);
  };
  render() {
    const {
      getTableProps,
      headerGroups,
      getTableBodyProps,
      instance,
      rows,
      tableRef,
      tableClassName,
      headerRef,
      headerGroupClassName,
      tableTheadClassName,
      tableBodyClassName,
      onScroll: onScrollFunc,
      paginate,
      FooterComponent,
      footerProps,
      noDataMessage,
      loading,
      loadingMessage,
      footerHeight = paginate ? 58 : 0,
      overscanRowCount,
      overscanColumnCount
    } = this.props;
    const headerHeight = this.headerCellMeasurerCache.rowHeight({
      index: 0
    });
    return /*#__PURE__*/_jsx(ScrollSync, {
      children: scrollParams => {
        const {
          onScroll,
          scrollLeft,
          scrollTop
        } = scrollParams;
        const proxiedOnScroll = new Proxy(onScroll, {
          apply: (target, thisArg, argumentsList) => {
            onScrollFunc(...argumentsList);
            return target(...argumentsList);
          }
        });
        const scrollProps = {};
        if (onScrollFunc) {
          scrollProps.onScroll = proxiedOnScroll;
          scrollProps.scrollTop = scrollTop;
        }
        return /*#__PURE__*/_jsx(AutoSizer, {
          children: ({
            height,
            width
          }) => /*#__PURE__*/_jsxs(Content, {
            children: [/*#__PURE__*/_jsx(Content.Area, {
              className: S.tableArea,
              children: /*#__PURE__*/_jsx("div", {
                ...getTableProps({
                  style: {
                    minWidth: width,
                    height,
                    overflow: 'hidden'
                  }
                }),
                ref: tableRef,
                className: tableClassName,
                children: loading ? /*#__PURE__*/_jsx(Loading, {
                  label: loadingMessage
                }) : /*#__PURE__*/_jsxs(React.Fragment, {
                  children: [/*#__PURE__*/_jsx("div", {
                    ref: headerRef,
                    className: tableTheadClassName,
                    children: headerGroups.map(headerGroup => {
                      const columns = headerGroup.headers;
                      const {
                        style,
                        key,
                        ...headerProps
                      } = headerGroup.getHeaderGroupProps({
                        className: classnames(headerGroupClassName, S.header)
                      });
                      return /*#__PURE__*/_createElement(Grid, {
                        ref: this.headerGridRef,
                        cellRenderer: this.headerCellRendererGetter(columns),
                        columnCount: columns.length,
                        columnWidth: this.columnWidthGetter(columns, width),
                        deferredMeasurementCache: this.headerCellMeasurerCache,
                        height: headerHeight,
                        rowCount: 1,
                        rowHeight: this.headerCellMeasurerCache.rowHeight,
                        width: width - this.headerScrollbarOffset,
                        onScroll: onScroll,
                        scrollLeft: scrollLeft,
                        overscanRowCount: 0,
                        overscanColumnCount: overscanColumnCount,
                        ...scrollProps,
                        ...headerProps,
                        key: `header-grid-${height}-${width}`
                      });
                    })
                  }), /*#__PURE__*/_createElement(Grid, {
                    ref: this.bodyGridRef,
                    scrollToAlignment: "center",
                    noContentRenderer: () => /*#__PURE__*/_jsx(NoData, {
                      label: noDataMessage
                    }),
                    cellRenderer: this.bodyCellRenderer,
                    cellRangeRenderer: this.cellRangeRenderer,
                    columnCount: instance.allColumns.length,
                    columnWidth: this.columnWidthGetter(instance.allColumns, width),
                    deferredMeasurementCache: this.bodyCellMeasurerCache,
                    height: height - headerHeight - footerHeight,
                    rowCount: rows.length,
                    onScroll: onScroll,
                    scrollLeft: scrollLeft,
                    ...scrollProps,
                    rowHeight: this.bodyCellMeasurerCache.rowHeight,
                    width: width,
                    overscanRowCount: overscanRowCount,
                    overscanColumnCount: overscanColumnCount,
                    ...getTableBodyProps(),
                    className: tableBodyClassName,
                    key: `grid-${height}-${width}`
                  })]
                })
              })
            }), paginate ? /*#__PURE__*/_jsx(Content.Footer, {
              children: FooterComponent ? renderDynamicComponent(FooterComponent, footerProps) : /*#__PURE__*/_jsx(TableV2Footer, {
                ...footerProps
              })
            }) : null]
          })
        });
      }
    });
  }
}
const resizerProxyEvents = ['onMouseDown', 'onTouchStart'];
const defaultSortByToggleProps = {
  style: {
    overflow: 'hidden'
  }
};
function HeaderCellRenderer(props) {
  const {
    columns,
    columnIndex,
    rowIndex,
    style,
    cellMeasurerCache,
    parent,
    headerClassName,
    headerFlexVariant,
    resizerClassName,
    sortAscClassName,
    sortDescClassName,
    onResizeStart
  } = props;
  const column = columns[columnIndex];
  const variant = headerFlexVariant || 'tableHeader';
  const {
    key: resizerKey,
    ...resizerProps
  } = column?.getResizerProps?.() || {};
  resizerProxyEvents.forEach(event => {
    if (resizerProps[event]) {
      const proxied = new Proxy(resizerProps[event], {
        apply: (target, thisArg, argumentsList) => {
          onResizeStart(...argumentsList);
          return target(...argumentsList);
        }
      });
      resizerProps[event] = proxied;
    }
  });
  const {
    key: headerKey,
    ...headerProps
  } = column.getHeaderProps({
    style
  });
  const {
    key: sortByToggleKey,
    ...sortByToggleProps
  } = column?.getSortByToggleProps?.(defaultSortByToggleProps) || defaultSortByToggleProps;
  return /*#__PURE__*/_jsx(CellMeasurer, {
    cache: cellMeasurerCache,
    columnIndex: columnIndex,
    parent: parent,
    rowIndex: rowIndex,
    children: /*#__PURE__*/_createElement("div", {
      ...headerProps,
      key: headerKey,
      className: classnames(headerClassName, column.isSorted ? column.isSortedDesc ? sortDescClassName : sortAscClassName : '')
    }, /*#__PURE__*/_createElement("div", {
      className: S.sortToggle,
      ...sortByToggleProps,
      key: sortByToggleKey
    }, /*#__PURE__*/_jsxs(Flex, {
      style: {
        height: '100%'
      },
      variant: variant,
      children: [column.render('Header'), column.canFilter && column.Filter && column.render('Filter')]
    })), column.canResize && /*#__PURE__*/_createElement("div", {
      ...resizerProps,
      className: classnames(resizerClassName, column.isResizing ? 'isResizing' : ''),
      key: resizerKey
    }))
  });
}